import { apiClient } from '@/services/api/apiClient';
import { ENDPOINTS } from '@/services/api/endpoints';
import { loadTaskForceChairDashboard as loadTaskForceChairDashboardCore } from '@/features/leadership/utils/taskForceChairDashboard';

/**
 * Normalize list-shaped API bodies (e.g. `{ users: T[] }`, `{ data: T[] }`) to `{ data: T[] }` for UI.
 */
function normalizeListResponse(body, arrayKeys = ['data', 'users', 'organizations']) {
    if (body == null) return { data: [] };
    if (Array.isArray(body)) return { data: body };
    for (const key of arrayKeys) {
        if (Array.isArray(body[key])) return { data: body[key] };
    }
    return { data: [] };
}

export const leadershipService = {
    // ───── Task Forces ─────
    /**
     * Without args: full list (legacy). With `{ page, limit, q }`: server pagination (limit max 9).
     */
    async getTaskForces(params = {}) {
        const usePagination =
            (params && typeof params === 'object' && !Array.isArray(params)
                && (params.page !== undefined
                    || params.limit !== undefined
                    || (params.q !== undefined && String(params.q).trim() !== '')));

        if (!usePagination) {
            const { data } = await apiClient.get(ENDPOINTS.TASK_FORCES.LIST);
            return normalizeListResponse(data, ['data', 'users']);
        }

        const { page = 1, limit = 9, q } = params;
        const query = {
            page,
            limit: Math.min(Math.max(1, Number(limit) || 9), 9),
        };
        if (q !== undefined && String(q).trim() !== '') {
            query.q = String(q).trim();
        }

        const { data } = await apiClient.get(ENDPOINTS.TASK_FORCES.LIST, { params: query });
        const body = data ?? {};
        if (Array.isArray(body.data) && typeof body.total === 'number') {
            return {
                data: body.data,
                total: body.total,
                page: body.page ?? page,
                limit: body.limit ?? query.limit,
            };
        }
        return normalizeListResponse(body, ['data', 'users']);
    },
    async getTaskForceById(id) {
        const { data } = await apiClient.get(ENDPOINTS.TASK_FORCES.DETAIL(id));
        return data;
    },
    async createTaskForce(payload) {
        const body = {
            name: String(payload.name ?? '').trim(),
            description: payload.description ?? payload.summary ?? '',
        };
        const { data } = await apiClient.post(ENDPOINTS.TASK_FORCES.CREATE, body);
        return data;
    },
    async updateTaskForce(tfId, patch) {
        const { data } = await apiClient.put(ENDPOINTS.TASK_FORCES.UPDATE(tfId), patch);
        return data;
    },
    async deleteTaskForce(tfId) {
        await apiClient.delete(ENDPOINTS.TASK_FORCES.DELETE(tfId));
        return { ok: true };
    },
    /** Links an account as the task force chair (`accountId` from user profile / accounts API). */
    async assignTaskForceToAccount(taskForceId, accountId) {
        const { data } = await apiClient.put(ENDPOINTS.TASK_FORCES.ASSIGN_ACCOUNT(taskForceId), {
            accountId,
        });
        return data;
    },
    /** Add one chair to a task force (task forces support multiple equal chairs). */
    async addTaskForceChair({ taskForceId, accountId }) {
        const { data } = await apiClient.put(ENDPOINTS.TASK_FORCES.ASSIGN_ACCOUNT(taskForceId), {
            accountId,
        });
        return data;
    },
    /** Remove one chair from a task force. */
    async removeTaskForceChair({ taskForceId, accountId }) {
        await apiClient.delete(ENDPOINTS.TASK_FORCES.REMOVE_CHAIR(taskForceId, accountId));
        return { ok: true };
    },
    /** List all chairs assigned to one task force. */
    async listChairsForTaskForce(taskForceId) {
        const { data } = await apiClient.get(ENDPOINTS.TASK_FORCES.CHAIRS(taskForceId));
        const chairs = data?.data ?? data;
        return Array.isArray(chairs) ? chairs : [];
    },
    async assignTaskForceChair(payload) {
        const accountId = payload.accountId;
        if (!accountId) {
            throw new Error('assignTaskForceChair requires accountId');
        }
        const { data } = await apiClient.put(ENDPOINTS.TASK_FORCES.ASSIGN_ACCOUNT(payload.taskForceId), {
            accountId,
        });
        return data;
    },
    /** Current user’s task force (from JWT). */
    async getMyTaskForce() {
        const { data } = await apiClient.get(ENDPOINTS.TASK_FORCES.MY);
        return data;
    },
    /** `params` optionally carries the dashboard date range (`startDate`/`endDate`). */
    async getTaskForceDashboard(taskForceId, params = {}) {
        const { data } = await apiClient.get(ENDPOINTS.TASK_FORCES.DASHBOARD(taskForceId), {
            params,
        });
        return data;
    },
    /** Authoritative pillar count for the task force (`prisma.pillar.count` scope). */
    async getTaskForcePillarCount(taskForceId) {
        const { data } = await apiClient.get(ENDPOINTS.TASK_FORCES.PILLAR_COUNT(taskForceId));
        const n = Number(data?.pillarCount);
        return Number.isFinite(n) ? n : 0;
    },
    async getTaskForcePillarDashboardSummary(taskForceId, params = {}) {
        const { data } = await apiClient.get(
            ENDPOINTS.TASK_FORCES.PILLAR_DASHBOARD_SUMMARY(taskForceId),
            { params },
        );
        return data;
    },
    async getTaskForceTaskVelocity(taskForceId, params = {}) {
        const { data } = await apiClient.get(ENDPOINTS.TASK_FORCES.TASK_VELOCITY(taskForceId), {
            params,
        });
        return data;
    },
    async getTaskForceTaskDistribution(taskForceId) {
        const { data } = await apiClient.get(ENDPOINTS.TASK_FORCES.TASK_DISTRIBUTION(taskForceId));
        return data;
    },
    /** Server-only aggregates: task force id/name list + summary metrics (no per-TF dashboard payloads). */
    async getExecutiveDashboard(params = {}) {
        const { data } = await apiClient.get(ENDPOINTS.EXECUTIVE.DASHBOARD, { params });
        const body = data ?? {};
        return {
            taskForces: body.taskForces ?? [],
            summary: body.summary ?? null,
        };
    },
    /** Task Force Chair home dashboard: task force + pillars with metrics (seed or project/task aggregation). */
    async loadTaskForceChairDashboard() {
        return loadTaskForceChairDashboardCore(
            () => this.getMyTaskForce(),
            (taskForceId) => this.getPillarsForTaskForce(taskForceId),
        );
    },

    // ───── Pillars (task-force scoped) ─────
    /**
     * Without `page`/`limit`, returns all pillars (array in `data`).
     * With pagination, API returns `{ data, pagination }`.
     */
    async getPillarsForTaskForce(taskForceId, opts = {}) {
        const params = {};
        if (opts.page != null) params.page = opts.page;
        if (opts.limit != null) params.limit = opts.limit;
        if (opts.q != null && String(opts.q).trim() !== '') params.q = String(opts.q).trim();
        const { data } = await apiClient.get(ENDPOINTS.HIERARCHY.PILLARS.LIST(taskForceId), {
            params,
        });
        if (data && typeof data === 'object' && !Array.isArray(data) && data.pagination) {
            return {
                data: Array.isArray(data.data) ? data.data : [],
                pagination: data.pagination,
            };
        }
        const list = Array.isArray(data) ? data : [];
        return { data: list };
    },
    async createPillar({ taskForceId, name, description, chairUserId, cityIds = [] }) {
        const chair = String(chairUserId ?? '').trim();
        const body = {
            name: String(name ?? '').trim(),
            description: description ?? '',
            // Only sent when a chair was chosen: a pillar may be created unchaired.
            ...(chair ? { chairUserId: chair } : {}),
            cityIds: Array.isArray(cityIds) ? cityIds : [],
        };
        const { data } = await apiClient.post(ENDPOINTS.HIERARCHY.PILLARS.CREATE(taskForceId), body);
        return data;
    },
    async updatePillar(taskForceId, pillarId, patch) {
        const { data } = await apiClient.put(
            ENDPOINTS.HIERARCHY.PILLARS.UPDATE(taskForceId, pillarId),
            patch,
        );
        return data;
    },
    async deletePillar(taskForceId, pillarId) {
        await apiClient.delete(ENDPOINTS.HIERARCHY.PILLARS.DELETE(taskForceId, pillarId));
        return { ok: true };
    },
    /** Legacy single-chair assign endpoint. Still supported by the backend as "add a chair". */
    async assignPillarChair({ taskForceId, pillarId, accountId }) {
        const { data } = await apiClient.put(ENDPOINTS.HIERARCHY.PILLARS.ASSIGN_CHAIR(taskForceId, pillarId), {
            accountId,
        });
        return data;
    },
    /** Add one chair to a pillar (pillars now support multiple equal chairs). */
    async addPillarChair({ taskForceId, pillarId, accountId }) {
        const { data } = await apiClient.post(ENDPOINTS.HIERARCHY.PILLARS.CHAIR(taskForceId, pillarId), {
            accountId,
        });
        return data;
    },
    /** Remove one chair from a pillar. */
    async removePillarChair({ taskForceId, pillarId, accountId }) {
        await apiClient.delete(ENDPOINTS.HIERARCHY.PILLARS.REMOVE_CHAIR(taskForceId, pillarId, accountId));
        return { ok: true };
    },
    /**
     * List all chairs assigned to one pillar (`GET .../pillar/:id/chair`, response `{ data: [account, ...] }`).
     * Named `listChairsForPillar` (not `listPillarChairs`) to avoid clashing with the existing
     * `listPillarChairs(params)` below, which lists directory users holding the Pillar Chair role.
     */
    async listChairsForPillar({ taskForceId, pillarId }) {
        const { data } = await apiClient.get(ENDPOINTS.HIERARCHY.PILLARS.CHAIR(taskForceId, pillarId));
        const chairs = data?.data ?? data;
        return Array.isArray(chairs) ? chairs : [];
    },
    async assignCityToPillar(taskForceId, pillarId, cityId) {
        const { data } = await apiClient.put(
            ENDPOINTS.HIERARCHY.PILLARS.ASSIGN_CITY(taskForceId, pillarId, cityId),
        );
        return data;
    },
    async removeCityFromPillar(taskForceId, pillarId, cityId) {
        await apiClient.delete(ENDPOINTS.HIERARCHY.PILLARS.REMOVE_CITY(taskForceId, pillarId, cityId));
        return { ok: true };
    },

    // ───── Users ─────
    /**
     * `GET /api/v1/users` — forwards `params` as query string.
     * Common: `roleName` (directory role, case-insensitive), `organizationId`, `memberAssignableOnly`,
     * `page` / `limit` (pagination when either is set).
     */
    async listUsers(params = {}) {
        const { data } = await apiClient.get(ENDPOINTS.USERS.LIST, { params });
        const body = data ?? {};
        const users = Array.isArray(body.users)
            ? body.users
            : Array.isArray(body.data)
              ? body.data
              : [];
        const out = { data: users };
        if (body.pagination != null) {
            out.pagination = body.pagination;
        }
        return out;
    },
    async listAdminUsers(params = {}) {
        return this._listRoleEndpoint(ENDPOINTS.USERS.ADMIN_USERS, params);
    },
    async listExecutiveUsers(params = {}) {
        return this._listRoleEndpoint(ENDPOINTS.USERS.EXECUTIVE_USERS, params);
    },
    async listTaskForceChairs(params = {}) {
        return this._listRoleEndpoint(ENDPOINTS.USERS.TASKFORCE_USERS, params);
    },
    async listPillarChairs(params = {}) {
        return this._listRoleEndpoint(ENDPOINTS.USERS.PILLAR_USERS, params);
    },
    async listProjectManagers(params = {}) {
        return this._listRoleEndpoint(ENDPOINTS.USERS.PROJECTMANAGER_USERS, params);
    },
    async listMembers(params = {}) {
        return this._listRoleEndpoint(ENDPOINTS.USERS.MEMBER_USERS, params);
    },
    async _listRoleEndpoint(endpointUrl, params = {}) {
        const { roleName, ...restParams } = params;
        const { data } = await apiClient.get(endpointUrl, { params: restParams });
        const body = data ?? {};
        const users = Array.isArray(body.users) ? body.users : Array.isArray(body.data) ? body.data : [];
        const out = { data: users };
        if (body.pagination != null) {
            out.pagination = body.pagination;
        }
        return out;
    },
    /**
     * Paginated search via `GET /api/v1/users/search`.
     * `q` matches firstName, lastName, email, org phone (partial, case-insensitive).
     * With `emptyRoleOnly: true`, only users with no login account or no directory roles (AccountRoles).
     * With `roleName`, only users whose account has that directory role (e.g. Pillar Chair assign modal).
     */
    async searchUsers({
        q = '',
        page = 1,
        limit = 10,
        emptyRoleOnly = false,
        accountWithoutRolesOnly = false,
        organizationId,
        roleName,
    } = {}) {
        const { data } = await apiClient.get(ENDPOINTS.USERS.SEARCH, {
            params: {
                q,
                page,
                limit,
                emptyRoleOnly: emptyRoleOnly === true || emptyRoleOnly === 'true',
                accountWithoutRolesOnly:
                    accountWithoutRolesOnly === true || accountWithoutRolesOnly === 'true',
                ...(organizationId ? { organizationId } : {}),
                ...(roleName ? { roleName } : {}),
            },
        });
        const users = Array.isArray(data?.users) ? data.users : [];
        const pagination = data?.pagination ?? {
            page,
            limit,
            total: users.length,
            totalPages: 1,
        };
        return { users, pagination, message: data?.message };
    },

    /**
     * Admin portal → **Manage Task Force Chairs**: candidate search only.
     */
    async searchUsersForTaskForceChairDirectory({ q, page = 1, limit = 25 } = {}) {
        return this.searchUsersNoRole({
            q,
            page,
            limit,
        });
    },

    async searchUsersNoRole({
        q = '',
        page = 1,
        limit = 10,
        organizationId,
    } = {}) {
        const { data } = await apiClient.get(ENDPOINTS.USERS.SEARCH_NO_ROLE, {
            params: {
                q,
                page,
                limit,
                ...(organizationId ? { organizationId } : {}),
            },
        });
        const users = Array.isArray(data?.users) ? data.users : [];
        const pagination = data?.pagination ?? {
            page,
            limit,
            total: users.length,
            totalPages: 1,
        };
        return { users, pagination, message: data?.message };
    },

    async createUser(payload) {
        const { data } = await apiClient.post(ENDPOINTS.USERS.LIST, payload);
        return data;
    },
    async updateUser(userId, patch) {
        const { data } = await apiClient.put(ENDPOINTS.USERS.UPDATE(userId), patch);
        return data;
    },
    async deactivateUser(userId) {
        const { data } = await apiClient.put(ENDPOINTS.USERS.DEACTIVATE(userId));
        return data;
    },
    // ───── Roles (RBAC) ─────
    async listRoles() {
        const { data } = await apiClient.get(ENDPOINTS.ROLES.LIST);
        return normalizeListResponse(data, ['data', 'roles']);
    },
    async updateAccountRoles(accountId, roleIds) {
        const { data } = await apiClient.put(ENDPOINTS.ACCOUNTS.ROLES(accountId), {
            roleIds,
        });
        return data;
    },
    async addMemberRole(accountId) {
        const { data } = await apiClient.put(ENDPOINTS.ACCOUNTS.MEMBER_ROLE(accountId));
        return data;
    },
    async removeMemberRole(accountId) {
        const { data } = await apiClient.delete(ENDPOINTS.ACCOUNTS.MEMBER_ROLE(accountId));
        return data;
    },
    /** Creates login credentials and initial RBAC role (`roleId` from `listRoles()`). */
    async createAccount(payload) {
        const { data } = await apiClient.post(ENDPOINTS.ACCOUNTS.CREATE, payload);
        return data;
    },
    async getRolePermissionsCatalog() {
        const { data } = await apiClient.get(ENDPOINTS.ROLES.PERMISSIONS_CATALOG);
        return data;
    },
    async getRoleById(roleId) {
        const { data } = await apiClient.get(ENDPOINTS.ROLES.DETAIL(roleId));
        return data;
    },
    async createRole(payload) {
        const { data } = await apiClient.post(ENDPOINTS.ROLES.CREATE, payload);
        return data;
    },
    async updateRole(roleId, patch) {
        const { data } = await apiClient.put(ENDPOINTS.ROLES.UPDATE(roleId), patch);
        return data;
    },
    async deleteRole(roleId) {
        const { data } = await apiClient.delete(ENDPOINTS.ROLES.DELETE(roleId));
        return data;
    },
    /** Persists the permission matrix (mock API) or bitmask fields (real API: send numeric `permissions`). */
    async updateRolePermissions(roleId, body) {
        const { data } = await apiClient.put(ENDPOINTS.ROLES.UPDATE(roleId), body);
        return data;
    },

    // ───── Cities ─────
    async listCities() {
        const { data } = await apiClient.get(ENDPOINTS.CITIES.LIST);
        return normalizeListResponse(data, ['data', 'cities']);
    },
    async searchCities({ q = '', page = 1, limit = 10 } = {}) {
        const { data } = await apiClient.get(ENDPOINTS.CITIES.SEARCH, {
            params: { q, page, limit },
        });
        const cities = Array.isArray(data?.cities) ? data.cities : [];
        const pagination = data?.pagination ?? {
            page,
            limit,
            total: cities.length,
            totalPages: 1,
        };
        return { cities, pagination, message: data?.message };
    },
    async createCity(payload) {
        const { data } = await apiClient.post(ENDPOINTS.CITIES.CREATE, payload);
        return data;
    },
    async updateCity(cityId, patch) {
        const { data } = await apiClient.put(ENDPOINTS.CITIES.UPDATE(cityId), patch);
        return data;
    },
    async deleteCity(cityId) {
        const { data } = await apiClient.delete(ENDPOINTS.CITIES.DELETE(cityId));
        return data;
    },

    // ───── Organizations ─────
    async listOrganizations() {
        const { data } = await apiClient.get(ENDPOINTS.ORGANIZATIONS.LIST);
        return normalizeListResponse(data, ['data', 'organizations']);
    },
    async searchOrganizations({ q = '', page = 1, limit = 10 } = {}) {
        const { data } = await apiClient.get(ENDPOINTS.ORGANIZATIONS.SEARCH, {
            params: { q, page, limit },
        });
        const organizations = Array.isArray(data?.organizations) ? data.organizations : [];
        const pagination = data?.pagination ?? {
            page,
            limit,
            total: organizations.length,
            totalPages: 1,
        };
        return { organizations, pagination, message: data?.message };
    },
    async createOrganization(payload) {
        const { data } = await apiClient.post(ENDPOINTS.ORGANIZATIONS.CREATE, payload);
        return data;
    },
    async updateOrganization(orgId, patch) {
        const { data } = await apiClient.put(ENDPOINTS.ORGANIZATIONS.UPDATE(orgId), patch);
        return data;
    },
    async deleteOrganization(orgId) {
        const { data } = await apiClient.delete(ENDPOINTS.ORGANIZATIONS.DELETE(orgId));
        return data;
    },

    async getAiSummary() {
        const { data: body } = await apiClient.get(ENDPOINTS.EXECUTIVE.AI_SUMMARY);
        return body?.data ?? null;
    },

    /** Cached AI briefing for a task force (executive drill-down; scope `taskforce:<id>`). */
    async getExecutiveTaskForceAiSummary(taskForceId) {
        const { data: body } = await apiClient.get(
            ENDPOINTS.EXECUTIVE.TASK_FORCE_AI_SUMMARY(taskForceId),
        );
        return body?.data ?? null;
    },

    /** Cached AI briefing for a pillar under a task force (executive drill-down; scope `pillar:<id>`). */
    async getExecutivePillarAiSummary(taskForceId, pillarId) {
        const { data: body } = await apiClient.get(
            ENDPOINTS.EXECUTIVE.PILLAR_AI_SUMMARY(taskForceId, pillarId),
        );
        return body?.data ?? null;
    },

    /** Cached AI briefing for the current user's task force (`GET .../task-force-chair/my/ai-summary`). */
    async getTaskForceAiSummary() {
        const { data: body } = await apiClient.get(ENDPOINTS.TASK_FORCES.MY_AI_SUMMARY);
        return body?.data ?? null;
    },
};

export default leadershipService;
